import { useState } from "react";

/* A dismissible card shown once per browser, keyed by `id`.
 *
 * Deliberately NOT a modal. A modal on first open blocks the thing the person
 * came to do and gets dismissed unread; an inline card sits above the content
 * and can be ignored by anyone who already knows what they're doing.
 *
 * localStorage, not a server flag — this is guidance, not state worth a table,
 * and the worst case is someone seeing it again on a new device. */
const KEY = (id) => `ssa_seen_${id}`;

function alreadySeen(id) {
  try {
    return localStorage.getItem(KEY(id)) === "1";
  } catch {
    // Private browsing can throw on access. Showing the card is the safe
    // failure — worse to crash than to over-explain.
    return false;
  }
}

export function FirstRun({ id, title, children, tone = "blue" }) {
  const [dismissed, setDismissed] = useState(() => alreadySeen(id));
  if (dismissed) return null;

  const tones = {
    blue: "bg-blue-50 border-blue-200 text-blue-900",
    slate: "bg-slate-50 border-slate-200 text-slate-800",
  };

  function dismiss() {
    try {
      localStorage.setItem(KEY(id), "1");
    } catch {
      /* still dismiss for this session */
    }
    setDismissed(true);
  }

  return (
    <div className={`relative rounded-xl border px-4 py-3 ${tones[tone] || tones.blue}`}>
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute top-2 right-2 h-6 w-6 rounded-full text-current/60 hover:bg-black/5"
      >
        ×
      </button>
      {title && <p className="pr-6 font-semibold">{title}</p>}
      <div className="mt-1 space-y-1 text-sm leading-relaxed pr-6">{children}</div>
      <button onClick={dismiss} className="mt-2 text-sm font-semibold underline">
        Got it
      </button>
    </div>
  );
}
